import { ChangeEvent, ReactElement } from 'react';

type RadioButtonGroupProps<T> = {
  selectedOption?: string;
  setSelectedOption: (inp: string) => void;
  options: T[];
  label?: string;
  spacing?: string; // Add spacing prop
  avoidMarginTop?: boolean;
  valueKey?: string;
  labelKey?: string;
  direction?: 'row' | 'col';
};

/**
 * A component that represents a radio button group with options.
 * @param props - The component's props.
 * @param props.selectedOption - The currently selected option.
 * @param props.setSelectedOption - A function to set the selected option.
 * @param props.options - The array of options to be displayed.
 * @param props.avoidMarginTop - Weather to avoid margin from top.
 * @param props.valueKey - Key of object if not an plain array.
 * @param props.labelKey - Key of the label of object if not an plain array.
 * @param [props.label] - The label for the radio button group.
 * @param [props.spacing] - The spacing between options. Default is 'mt-2'.
 * @param props.direction - Direction of the buttons.
 * @returns A React element representing the radio button group.
 */
export default function RadioButtonGroup<T>({
  selectedOption,
  setSelectedOption,
  options,
  label,
  avoidMarginTop,
  valueKey,
  labelKey,
  direction = 'row',
  spacing = 'mr-4' // Set default value for spacing prop
}: RadioButtonGroupProps<T>): ReactElement {
  /**
   * Handles the change event when a radio button option is selected.
   * @param event - The change event.
   */
  const handleOptionChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSelectedOption(event.target.value);
  };

  return (
    <div className={`relative ${!avoidMarginTop && 'mt-4'}`}>
      <div className="block text-gray-500 dark:text-gray-500 text-base font-medium">{label}</div>

      <div
        className={`flex text-base font-normal text-gray-300 dark:text-gray-300 flex-wrap ${
          direction === 'col' ? 'flex-col' : 'flex-row'
        }`}
      >
        {options.map((option) => (
          <label
            htmlFor={`radio-btn-${valueKey ? (option as any)[valueKey] : option}`}
            key={(option as any).toString()}
            className={`inline-flex cursor-pointer items-center mt-2 ${spacing}`}
          >
            <input
              type="radio"
              id={`radio-btn-${(option as any).toString()}`}
              value={valueKey ? (option as any)[valueKey] : option}
              checked={selectedOption === (valueKey ? (option as any)[valueKey] : option)}
              onChange={handleOptionChange}
              className="form-radio text-primary-600 h-5 w-5 bg-container-500 mr-2 cursor-pointer"
            />
            {labelKey ? (option as any)[labelKey] : option}
          </label>
        ))}
      </div>
    </div>
  );
}
